import Chart from "react-apexcharts";
import { reduzirNome } from "../../../utils/reduzirNome";
import { Loading } from "../../../componentes/loading/loading";
import { useEffect, useState } from "react";

const ContaContabilAreaChart = ({ relatorio = {}, loading = false, ano, onBarClick }) => {
  const safeNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const [ctrlPressionado, setCtrlPressionado] = useState(false);

  useEffect(() => {
    const keyDown = (e) => setCtrlPressionado(e.ctrlKey);
    const keyUp = (e) => setCtrlPressionado(e.ctrlKey);

    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);

    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    };
  }, []);

  const anos = Array.isArray(ano) ? ano.map(Number) : [];

  const orcadoObj = relatorio?.totalOrcadoPorContaContabil ?? {};
  const realizadoObj = relatorio?.totalRealizadoPorContaContabil ?? {};
  const realizadoAnteriorObj =
    relatorio?.totalRealizadoPorContaContabilAnterior ?? {};

  const contasOrdenadas = Object.keys({
    ...orcadoObj,
    ...realizadoObj,
  })
    .map((conta) => {
      const orcado = safeNumber(orcadoObj[conta]);
      const realizado = safeNumber(realizadoObj[conta]);
      const realizadoAnterior = safeNumber(realizadoAnteriorObj[conta]);

      return {
        conta,
        orcado,
        realizado,
        realizadoAnterior,
        total: orcado + realizado,
      };
    })

    .filter((item) => item.total > 0)
    // ordena por impacto
    .sort((a, b) => b.total - a.total)
    // pega só as 20 primeiras
    .slice(0, 20);

  /* ============================
   * Séries
   * ============================ */
  const dataOrcado = contasOrdenadas.map((i) => i.orcado);
  const dataRealizado = contasOrdenadas.map((i) => i.realizado);
  const dataRealizadoAnterior = contasOrdenadas.map(
    (i) => i.realizadoAnterior
  );

  const todosZerados = [...dataOrcado, ...dataRealizado].every(
    (v) => v === 0
  );

  const somente2026 = anos.length === 1 && anos.includes(2026);
  const temAnoAnterior = anos.some((a) => a < 2026);

  const series = [
    { name: "Orçado", data: dataOrcado },
    { name: "Realizado", data: dataRealizado },
    ...(somente2026 && !temAnoAnterior
      ? [{ name: "Realizado em 2025", data: dataRealizadoAnterior }]
      : []),
  ];

  /* ============================
   * Configuração ApexCharts
   * ============================ */
  const options = {
    chart: {
      type: "area",
      toolbar: { show: true },
      animations: { enabled: false }, // 🔥 evita parser error
      events: onBarClick
        ? {
          dataPointSelection: (_event, _chartContext, config) => {
            const item = contasOrdenadas[config.dataPointIndex];
            if (!item) return;

            const codigo = item.conta.includes(" - ")
              ? item.conta.split(" - ")[0].trim()
              : item.conta;

            onBarClick(codigo);
          },
        }
        : undefined,
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 5,
      hover: { size: 9 },
    },
    colors: ["#271de0", "#e03737", "#ff9900"],
    xaxis: {
      categories: contasOrdenadas.map((i) =>
        reduzirNome ? reduzirNome(i.conta) : i.conta
      ),
      labels: {
        style: { fontSize: "12px" },
        rotate: -45,
        trim: true,
      },
    },
    yaxis: {
      tickAmount: 10,
      labels: {
        formatter: (value) =>
          new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
            maximumFractionDigits: 0,
          }).format(value),
      },
    },
    tooltip: {
      intersect: ctrlPressionado,
      shared: !ctrlPressionado,
      y: {
        formatter: (val) =>
          new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(val),
      },
    },
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "12px",
    },
  };

  return (
    <div className={`min-h-[326px] ${onBarClick ? "cursor-pointer" : ""}`}>
      {loading ? (
        <div className="flex justify-center items-center">
          <Loading />
        </div>
      ) : todosZerados ? (
        <p className="text-muted-foreground text-center">
          Não há dados disponíveis para exibir o gráfico.
        </p>
      ) : (
        <Chart
          options={options}
          series={series}
          type="area"
          height={310}
          width="100%"
        />
      )}
    </div>
  );
};

export default ContaContabilAreaChart;
